import { formatDate } from "@/lib/formatters";
import { ContractChange, formatFieldName } from "@/hooks/useContractChanges";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, DollarSign, User, RefreshCw, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useState, useEffect } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatCurrency } from "@/lib/formatters";
import { useCurrencies } from "@/hooks/useCurrencies";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { currencyConverter } from "@/services/currencyConverter";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { Json } from "@/integrations/supabase/types";
import { useTranslation } from "react-i18next";

interface TimelineEntryProps {
  change: ContractChange;
}

export function TimelineEntry({ change }: TimelineEntryProps) {
  const { t } = useTranslation();
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [conversionRate, setConversionRate] = useState<number>(1);
  const [isConverting, setIsConverting] = useState(false);
  
  const { canUseCurrencyConversion } = useFeatureAccess();
  const { useCurrencyQuery } = useCurrencies();
  const { useUserPreferencesQuery } = useUserPreferences();
  
  const { data: userPreferences } = useUserPreferencesQuery();

  const getCurrencyId = (): string | undefined => {
    const metadata = change.metadata;
    
    if (metadata && typeof metadata === 'object') {
      if ('currencyId' in metadata) {
        return metadata.currencyId as string;
      }
    }
    
    if (change.change_type === 'create' && change.new_value && typeof change.new_value === 'object') {
      if ('currencyId' in change.new_value) {
        return change.new_value.currencyId as string;
      }
    }

    console.log("Could not determine currency ID from change:", change);
    return undefined;
  };

  const currencyId = getCurrencyId();
  console.log("Currency ID detected for timeline entry:", currencyId);
  
  const { data: currency } = useCurrencyQuery(currencyId || '');
  const { data: defaultCurrency } = useCurrencyQuery(userPreferences?.defaultCurrencyId || '');

  useEffect(() => {
    const convertCurrency = async () => {
      if (!canUseCurrencyConversion || !currency || !defaultCurrency || !currencyId || !userPreferences?.defaultCurrencyId) {
        return;
      }

      if (currencyId === userPreferences.defaultCurrencyId) {
        return;
      }

      setIsConverting(true);
      try {
        const amount = change.change_type === 'create' 
          ? change.new_value?.expectedCost 
          : change.new_value;

        if (amount === undefined || amount === null) {
          console.log("No amount to convert", change);
          setIsConverting(false);
          return;
        }

        console.log(`Converting ${amount} from ${currencyId} to ${userPreferences.defaultCurrencyId}`);
        const result = await currencyConverter.convert(
          parseFloat(amount),
          currencyId,
          userPreferences.defaultCurrencyId
        );
        
        console.log("Conversion result:", result);
        setConvertedAmount(result.convertedAmount);
        setConversionRate(result.rate);
      } catch (error) {
        console.error("Error converting currency:", error);
        setConvertedAmount(null);
      } finally {
        setIsConverting(false);
      }
    };

    convertCurrency();
  }, [change, currency, defaultCurrency, currencyId, userPreferences?.defaultCurrencyId, canUseCurrencyConversion]);

  const formatCostValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return String(value);
    
    if (currency) {
      return formatCurrency(numValue, currency.code);
    } else {
      return numValue.toLocaleString();
    }
  };

  const isContractCreation = change.change_type === 'create';
  const isCostUpdate = change.change_type === 'update' && change.field_name === 'expectedCost';
  
  const getUserInitials = () => {
    if (change.user?.name) {
      return change.user.name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    
    if (change.user?.email) {
      return change.user.email.charAt(0).toUpperCase();
    }
    
    return 'U';
  };

  const renderConvertedAmount = (originalAmount: any) => {
    if (convertedAmount === null || !defaultCurrency || !canUseCurrencyConversion) {
      return null;
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 rounded-full cursor-help ml-2">
              <RefreshCw className="h-3 w-3 text-blue-500" />
              <span className="text-blue-700 dark:text-blue-300">
                {formatCurrency(convertedAmount, defaultCurrency.code)}
              </span>
              <AlertTriangle className="h-3 w-3 text-amber-500 ml-0.5 opacity-70" />
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-black text-white border-gray-800 p-3 max-w-[300px]">
            <div className="space-y-2">
              <p>{t('timeline.conversionRate', 'Converted at rate')}: 1 {currency?.code} = {conversionRate.toFixed(4)} {defaultCurrency.code}</p>
              <p className="text-amber-300 text-xs flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <span>{t('common.currencyDisclaimer', 'Currency conversions are estimates and may not reflect exact rates at the time of contract payment.')}</span>
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <Card className="mb-4 relative group hover:shadow-md transition-shadow duration-300">
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg bg-primary opacity-70 group-hover:opacity-100 transition-opacity" />
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-full bg-primary/10">
            <DollarSign className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-primary text-white">
                  {isContractCreation ? t('timeline.initialCostSet', 'Initial Cost Set') : t('timeline.costUpdated', 'Cost Updated')}
                </Badge>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(change.changed_at)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{new Date(change.changed_at).toLocaleString()}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              {change.user && (
                <div className="flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="text-xs">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-muted-foreground">
                    {change.user.name || change.user.email}
                  </span>
                </div>
              )}
            </div>
            
            {isCostUpdate && (
              <div className="mt-2 space-y-2">
                <div className="text-sm">
                  <span className="font-medium">{t('timeline.from', 'From')}: </span>
                  <span className="text-muted-foreground">
                    {formatCostValue(change.old_value)}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="font-medium">{t('timeline.to', 'To')}: </span>
                  <span className="text-muted-foreground">
                    {formatCostValue(change.new_value)}
                    {renderConvertedAmount(change.new_value)}
                  </span>
                </div>
                
                <div className="text-xs text-muted-foreground mt-2">
                  {t('timeline.costChange', 'Cost')} {
                    parseFloat(change.new_value) > parseFloat(change.old_value) 
                      ? t('timeline.increased', 'increased')
                      : t('timeline.decreased', 'decreased')
                  } {t('timeline.by', 'by')} {
                    formatCostValue(Math.abs(parseFloat(change.new_value) - parseFloat(change.old_value)))
                  }
                </div>
              </div>
            )}
            
            {isContractCreation && (
              <div className="mt-2 text-sm">
                <span className="font-medium">{t('timeline.initialCost', 'Initial Cost')}: </span>
                <span className="text-muted-foreground">
                  {change.new_value && change.new_value.expectedCost ? 
                    formatCostValue(change.new_value.expectedCost) : 
                    t('timeline.notSpecified', 'Not specified')}
                  {change.new_value && change.new_value.expectedCost && renderConvertedAmount(change.new_value.expectedCost)}
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
